import { useRef, useState } from 'react';
import clsx from 'clsx';
import Button from 'components/UI/V3/Button/Button';
import { OverFullWindow } from 'components/UI';
import { LoyaltyEditField, LoyaltyEditFieldConfig } from './LoyaltyEditField';
import styles from './LoyaltyEditLayout.module.scss';

interface LoyaltyEditLayoutProps {
    title: string;
    contentTitle?: string;
    description?: string;
    fields: Array<LoyaltyEditFieldConfig>;
    onClose: () => void;
    onSave: (changes: Record<string, string>) => void;
}

export const LoyaltyEditLayout = (props: LoyaltyEditLayoutProps) => {
    const initialValues = useRef<Record<string, string>>(
        props.fields.reduce<Record<string, string>>((values, field) => {
            values[field.key] = field.value;
            return values;
        }, {})
    ).current;

    const [values, setValues] = useState<Record<string, string>>(initialValues);

    const handleFieldChange = (key: string, value: string) => {
        setValues(current => ({ ...current, [key]: value }));
    };

    const handleSave = () => {
        const changes: Record<string, string> = {};

        Object.keys(values).forEach(key => {
            // A cleared numeric field is not a change to 0: keep the current value instead.
            if (values[key] !== initialValues[key] && values[key] !== '') {
                changes[key] = values[key];
            }
        });

        props.onSave(changes);
    };

    const intro = props.contentTitle && (
        <div className={styles.intro}>
            <h2 className={styles.contentTitle}>{props.contentTitle}</h2>
            {props.description && <p className={styles.description}>{props.description}</p>}
        </div>
    );

    const fields = (
        <div className={clsx(styles.fields, { [styles.singleField]: props.fields.length === 1 })}>
            {props.fields.map(field => (
                <LoyaltyEditField
                    key={field.key}
                    field={{
                        ...field,
                        value: values[field.key],
                        onChange: value => handleFieldChange(field.key, value)
                    }}
                />
            ))}
        </div>
    );

    const actions = (
        <div className={styles.actions}>
            <Button
                label="Save"
                size="large"
                className={styles.saveButton}
                onClick={handleSave}
            />
        </div>
    );

    return (
        <OverFullWindow
            title={props.title}
            onClose={props.onClose}
            centered
            showMobileLogo
            detailContainerClassName={styles.detailContainer}
        >
            <main className={styles.body}>
                <div className={styles.form}>
                    {intro}
                    {fields}
                    {actions}
                </div>
            </main>
        </OverFullWindow>
    );
};
